import React, { useState, useEffect, useMemo } from "react";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Select, MenuItem } from "@mui/material";
import MainBox from "../../../../container/MainBox";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import { selectSx, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getPayrollRuns, getPayrollRunById } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money, periodLabel } from "../payrollUtils";
import { tableScrollSx } from "../../../../container/frameLayout";

const nameOf = (it) => it.employee ? `${it.employee.fname || ""} ${it.employee.lname || ""}`.trim() : (it.employeeName || "—");
const ded = (it, re) => (it.deductions || []).filter((d) => re.test(d.name)).reduce((s, d) => s + (Number(d.amount) || 0), 0);
const emp = (it, re) => (it.employerContributions || []).filter((d) => re.test(d.name)).reduce((s, d) => s + (Number(d.amount) || 0), 0);

// columns: PF (EE/ER), ESI (EE/ER), PT, TDS
const COLS = [
  { key: "pfEE", label: "PF — Employee", src: (it) => ded(it, /EPF/i) },
  { key: "pfER", label: "PF — Employer", src: (it) => emp(it, /EPF/i) },
  { key: "esiEE", label: "ESI — Employee", src: (it) => ded(it, /ESI/i) },
  { key: "esiER", label: "ESI — Employer", src: (it) => emp(it, /ESI/i) },
  { key: "pt", label: "Prof. Tax", src: (it) => ded(it, /Professional Tax/i) },
  { key: "tds", label: "TDS", src: (it) => ded(it, /TDS/i) },
];

const StatutoryReport = () => {
  const notify = useNotify();
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRun, setLoadingRun] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getPayrollRuns();
        const list = (Array.isArray(res?.data) ? res.data : []).filter((r) => ["approved", "paid"].includes(r.status));
        if (!active) return;
        setRuns(list);
        if (list[0]) setRunId(list[0]._id);
      } catch (e) {
        notify.error(e?.message || "Failed to load runs");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!runId) { setRun(null); return; }
    let active = true;
    (async () => {
      setLoadingRun(true);
      try {
        const res = await getPayrollRunById(runId);
        if (active) setRun(res?.data || null);
      } finally {
        if (active) setLoadingRun(false);
      }
    })();
    return () => { active = false; };
  }, [runId]);

  const rows = useMemo(() => (run?.items || []).map((it) => {
    const vals = {}; COLS.forEach((c) => { vals[c.key] = c.src(it); });
    return { name: nameOf(it), vals };
  }), [run]);

  const totals = useMemo(() => {
    const t = {}; COLS.forEach((c) => { t[c.key] = rows.reduce((s, r) => s + r.vals[c.key], 0); });
    return t;
  }, [rows]);

  const exportCsv = () => {
    const header = ["Employee", ...COLS.map((c) => c.label)];
    const body = rows.map((r) => [r.name, ...COLS.map((c) => r.vals[c.key])]);
    const totalRow = ["TOTAL", ...COLS.map((c) => totals[c.key])];
    const csv = [header, ...body, totalRow].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `statutory_${periodLabel(run.month, run.year).replace(" ", "_")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <SectionLoader minHeight="40vh" />;

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Statutory Report</Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Select value={runId} onChange={(e) => setRunId(e.target.value)} size="small" displayEmpty sx={{ ...selectSx, minWidth: "12rem" }}>
            {runs.length === 0 && <MenuItem value="">No approved runs</MenuItem>}
            {runs.map((r) => <MenuItem key={r._id} value={r._id}>{periodLabel(r.month, r.year)}</MenuItem>)}
          </Select>
          <BtnSecondary onClick={exportCsv} disabled={!run || rows.length === 0} sx={{ borderRadius: "8px" }}>Export CSV</BtnSecondary>
        </Stack>
      </Stack>

      {runs.length === 0 ? (
        <EmptyState title="No approved runs" description="Approve a payroll run to generate statutory reports." />
      ) : loadingRun ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ minWidth: "52rem" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Employee</TableCell>
                {COLS.map((c) => <TableCell key={c.key} sx={headCellSx} align="right">{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody><TableSkeleton columns={2} /></TableBody>
          </Table>
        ) : rows.length === 0 ? (
        <EmptyState title="No data" />
      ) : (
        // The frame's one exception to "a card never scrolls internally": a
        // wide table scrolls sideways inside its own card, never by moving the
        // page, and its first column pins so a row whose employee has scrolled
        // off the left is still readable.
        <Box sx={tableScrollSx()}>
          <Table sx={{ minWidth: "52rem" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Employee</TableCell>
                {COLS.map((c) => <TableCell key={c.key} sx={headCellSx} align="right">{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} hover sx={{ "&:hover": { background: tokens.surfaceSubtle } }}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.name}</TableCell>
                  {COLS.map((c) => <TableCell key={c.key} sx={bodyCellSx} align="right">{r.vals[c.key] ? money(r.vals[c.key]) : "—"}</TableCell>)}
                </TableRow>
              ))}
              <TableRow sx={{ background: tokens.surfaceSubtle }}>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>Total</TableCell>
                {COLS.map((c) => <TableCell key={c.key} sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{money(totals[c.key])}</TableCell>)}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </MainBox>
  );
};

export default StatutoryReport;
